// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module createIdentify
 * @package @geenius/analytics/solidjs-css
 * @description Wraps identity-management actions for the SolidJS CSS variant
 * so consumers can identify or reset a user without re-plumbing tracker access.
 */

import type { UserTraits } from "@geenius/analytics";
import { createAnalytics } from "./createAnalytics";

/**
 * Identity-management helpers returned by {@link createIdentify}.
 *
 * @property identify Associates the current session with a user and traits.
 * @property reset Clears the current analytics identity and session state.
 */
export interface CreateIdentifyReturn {
  identify: (userId: string, traits?: UserTraits) => Promise<void>;
  reset: () => Promise<void>;
}

/**
 * Returns identify and reset helpers backed by the active analytics tracker.
 *
 * @returns Identity-management functions for the active tracker.
 *
 * @example
 * ```tsx
 * const { identify } = createIdentify()
 * void identify('user_123', { plan: 'pro' })
 * ```
 */
export function createIdentify(): CreateIdentifyReturn {
  const analytics = createAnalytics();

  return {
    identify: (userId: string, traits?: UserTraits) =>
      analytics.identify(userId, traits),
    reset: () => analytics.reset(),
  };
}
